<script setup>
// Lead Workspace — Activity tab. Reuses Nexceo::LeadActivity via
// Nexceo::ActivitiesController#index (now with an optional ?type= filter —
// see the controller's ACTIVITY_FILTER_GROUPS). Filter groups only cover
// activity_types that are actually ever logged; there is no
// WhatsApp-message/email/SMS/note activity_type today (those live in the
// linked conversation — see the Communications tab), so no filter is shown
// for them here rather than shipping one that always returns empty.
import { ref, computed, onMounted, watch } from 'vue';
import leadsAPI from 'dashboard/api/nexceo/leads';
import MessageApi from 'dashboard/api/inbox/message';
import { formatDateTime, timeAgo, humanize } from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
});

const FILTERS = [
  { key: null, label: 'All Activity', icon: 'i-lucide-list' },
  { key: 'calls', label: 'Calls', icon: 'i-lucide-phone' },
  { key: 'site_visits', label: 'Site Visits', icon: 'i-lucide-calendar-check' },
  { key: 'documents', label: 'Documents', icon: 'i-lucide-file-text' },
  { key: 'tasks', label: 'Tasks', icon: 'i-lucide-list-checks' },
  { key: 'stage_changes', label: 'Stage Changes', icon: 'i-lucide-arrow-right-left' },
  { key: 'ai_actions', label: 'AI Actions', icon: 'i-lucide-bot' },
];

// Mirrors Nexceo::ActivitiesController::ACTIVITY_FILTER_GROUPS — kept in
// sync manually since it's a small, stable list; used client-side to bucket
// the summary tile counts without a second request per filter.
const ACTIVITY_FILTER_GROUPS = {
  calls: ['call_started', 'call_answered', 'call_missed', 'call_rejected', 'call_completed', 'call_failed', 'call_brief_generated'],
  site_visits: ['site_visit_scheduled', 'site_visit_completed', 'site_visit_cancelled', 'meeting_brief_generated'],
  documents: ['document_shared', 'document_resent', 'document_share_followup_sent'],
  tasks: ['human_task_created', 'human_task_completed', 'human_task_cancelled', 'human_task_outcome_recorded'],
  stage_changes: ['stage_changed'],
  ai_actions: ['ai_replied', 'human_handoff', 'workforce_event', 'action_blocked', 'project_routed',
    'followup_skipped_stale_context', 'ai_qualification_followup_sent', 'appointment_customer_reminder_sent',
    'appointment_agent_reminder_sent', 'human_followup_due_notified', 'system_corrected', 'decision_memory_added'],
};

const activeFilter = ref(null);
const activities = ref([]);
const allActivities = ref([]);
const isLoading = ref(true);
const recentNotes = ref([]);

async function loadAllForSummary() {
  try {
    const { data } = await leadsAPI.getActivities(props.lead.id);
    allActivities.value = data ?? [];
  } catch {
    allActivities.value = [];
  }
}

async function loadRecentNotes() {
  if (!props.lead.conversation_id) return;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: props.lead.conversation_id,
      after: 0,
      before: Number.MAX_SAFE_INTEGER,
    });
    const list = Array.isArray(data) ? data : (data?.payload ?? []);
    recentNotes.value = list
      .filter(m => m.private)
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, 3);
  } catch {
    recentNotes.value = [];
  }
}

async function load() {
  isLoading.value = true;
  try {
    const { data } = await leadsAPI.getActivities(props.lead.id, activeFilter.value);
    activities.value = data ?? [];
  } catch {
    activities.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(activeFilter, load);
onMounted(() => {
  load();
  loadAllForSummary();
  loadRecentNotes();
});

const groupedByDay = computed(() => {
  const groups = {};
  activities.value.forEach(a => {
    const day = new Date(a.created_at).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    groups[day] = groups[day] || [];
    groups[day].push(a);
  });
  return groups;
});

// Summary tile grid, bucketed by the same groups as the left-hand filters —
// always computed off the full unfiltered activity list (allActivities), not
// the currently-filtered timeline, so switching filters doesn't zero it out.
const summaryTiles = computed(() =>
  FILTERS.filter(f => f.key).map(f => {
    const types = ACTIVITY_FILTER_GROUPS[f.key] || [];
    const count = allActivities.value.filter(a => types.includes(a.activity_type)).length;
    return { ...f, count };
  })
);

const ACTOR_ICON = { ai_employee: 'i-lucide-bot', user: 'i-lucide-user', system: 'i-lucide-settings' };
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-4 p-4">
    <!-- Filters -->
    <div class="rounded-xl border border-n-weak bg-n-background p-3 h-fit">
      <h3 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide px-1 mb-2">
        Activity Filters
      </h3>
      <button
        v-for="f in FILTERS"
        :key="f.label"
        class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left"
        :class="
          activeFilter === f.key
            ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="activeFilter = f.key"
      >
        <span :class="f.icon" class="size-3.5 shrink-0" />
        {{ f.label }}
      </button>
    </div>

    <!-- Timeline -->
    <div class="rounded-xl border border-n-weak bg-n-background p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Activity Timeline</h3>
      <div v-if="isLoading" class="text-sm text-n-slate-8">Loading…</div>
      <div v-else-if="!activities.length" class="text-sm text-n-slate-8">
        No activity recorded yet for this filter.
      </div>
      <div v-else class="space-y-5">
        <div v-for="(items, day) in groupedByDay" :key="day">
          <p class="text-xs font-semibold text-n-slate-9 mb-2">{{ day }}</p>
          <div class="relative pl-5 border-l border-n-weak space-y-3">
            <div v-for="a in items" :key="a.id" class="relative">
              <span
                class="absolute -left-[21px] top-1 size-2.5 rounded-full bg-woot-500 ring-2 ring-n-background"
              />
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ a.friendly_title || humanize(a.activity_type) }}
                  </p>
                  <p v-if="a.description" class="text-xs text-n-slate-9 mt-0.5">
                    {{ a.description }}
                  </p>
                </div>
                <span class="text-xs text-n-slate-8 whitespace-nowrap shrink-0">
                  {{ formatDateTime(a.created_at) }}
                </span>
              </div>
              <span
                class="inline-flex items-center gap-1 text-[10px] text-n-slate-8 mt-1"
              >
                <span :class="ACTOR_ICON[a.actor_type] || 'i-lucide-user'" class="size-3" />
                {{ a.actor_name || humanize(a.actor_category) || humanize(a.actor_type) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary + Recent Notes -->
    <div class="flex flex-col gap-4">
      <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Activity Summary</h3>
        <div v-if="!allActivities.length" class="text-xs text-n-slate-8">
          Nothing logged yet.
        </div>
        <div v-else class="grid grid-cols-2 gap-2">
          <div
            v-for="tile in summaryTiles"
            :key="tile.key"
            class="rounded-lg bg-n-alpha-1 p-2.5 cursor-pointer"
            @click="activeFilter = tile.key"
          >
            <div class="flex items-center gap-1.5 text-n-slate-9">
              <span :class="tile.icon" class="size-3.5" />
              <span class="text-[11px]">{{ tile.label }}</span>
            </div>
            <p class="text-lg font-semibold text-n-slate-12 mt-0.5">{{ tile.count }}</p>
          </div>
        </div>
      </div>

      <div v-if="lead.conversation_id" class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-n-slate-12">Recent Notes</h3>
          <router-link
            :to="{ name: 'nexceo_lead_workspace', params: { id: lead.id, tab: 'notes' } }"
            class="text-xs text-woot-600 hover:underline"
          >
            View All
          </router-link>
        </div>
        <p v-if="!recentNotes.length" class="text-xs text-n-slate-8">No notes yet.</p>
        <ul v-else class="space-y-2">
          <li v-for="n in recentNotes" :key="n.id" class="text-xs">
            <p class="text-n-slate-12 line-clamp-2">{{ n.content }}</p>
            <p class="text-n-slate-8 mt-0.5">{{ n.sender?.name || 'Team' }} · {{ timeAgo(n.created_at * 1000) }}</p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
